// React
import React, {
  createElement,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { Check, ChevronRight } from "@mui/icons-material";
// MUI
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from "@mui/material";

// Hooks
import { hotkeyToText } from "hooks/ui/hotkeys";

// Styles
import { colors, radii, shadows, zIndex } from "styles/tokens";

const SUBMENU_CLOSE_DELAY_MS = 200;

// Same look as the root menu's paper (themes.jsx MuiMenu override), for the submenus
export const menuPaperSx = {
  minWidth: 220,
  maxWidth: 320,
  borderRadius: `${radii.card}px`,
  border: `1px solid ${colors.gray200}`,
  boxShadow: shadows.popover,
  backgroundColor: colors.white,
  // Keeps the list scrollbar inside the rounded corners
  overflow: "hidden",
};

const shortcutSx = {
  ml: 3,
  pl: 1,
  flexShrink: 0,
  fontSize: 12,
  lineHeight: "20px",
  letterSpacing: "0.02em",
  color: colors.gray400,
  fontFamily: "inherit",
  fontVariantNumeric: "tabular-nums",
};

const shortcutText = (shortcut) => {
  if (!shortcut) return null;
  if (Array.isArray(shortcut)) return hotkeyToText(shortcut);
  // "⌘C" is already a label; "mod+c", "shift+enter", "g c" are specs
  return /[+\s]/.test(shortcut) || /^[a-z0-9]+$/i.test(shortcut)
    ? hotkeyToText(shortcut)
    : shortcut;
};

const renderIcon = (icon) => {
  if (!icon) return null;
  if (isValidElement(icon)) return icon;
  return createElement(icon, { fontSize: "small" });
};

// One submenu open per level, with a short delay before closing so the pointer can travel
// diagonally from the parent item to the submenu
export function useSubmenuLevel() {
  const [sub, setSub] = useState(null); // { id, anchorEl, focusFirst }
  const timer = useRef(null);
  const cancel = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
  }, []);
  const openSub = useCallback(
    (id, anchorEl, focusFirst = false) => {
      cancel();
      setSub((current) =>
        current && current.id === id && current.anchorEl === anchorEl && !focusFirst
          ? current
          : { id, anchorEl, focusFirst }
      );
    },
    [cancel]
  );
  const closeSub = useCallback(() => {
    cancel();
    setSub(null);
  }, [cancel]);
  const scheduleClose = useCallback(() => {
    cancel();
    timer.current = setTimeout(() => setSub(null), SUBMENU_CLOSE_DELAY_MS);
  }, [cancel]);
  useEffect(() => cancel, [cancel]);
  return { sub, openSub, closeSub, scheduleClose, cancel };
}

function Submenu({ item, anchorEl, focusFirst, onRun, onRequestClose, onPointerEnter }) {
  const level = useSubmenuLevel();
  const handleKeyDown = (event) => {
    if (event.key === "ArrowLeft" || event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onRequestClose(true);
      return;
    }
    // Tab closes the whole menu (handled by the root list); other keys stay in this list
    if (event.key !== "Tab") event.stopPropagation();
  };
  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="right-start"
      sx={{ zIndex: zIndex.submenu }}
      modifiers={[
        { name: "offset", options: { offset: [-5, 2] } },
        { name: "flip", options: { fallbackPlacements: ["left-start", "right-end", "left-end"] } },
        { name: "preventOverflow", options: { padding: 8 } },
      ]}
      onMouseEnter={onPointerEnter}
    >
      <Paper elevation={0} sx={menuPaperSx}>
        <MenuList
          dense
          autoFocusItem={focusFirst}
          variant="menu"
          aria-label={typeof item.label === "string" ? item.label : undefined}
          onKeyDown={handleKeyDown}
          sx={{ p: "4px", maxHeight: "min(60dvh, 480px)", overflowY: "auto", overscrollBehavior: "contain" }}
        >
          {renderEntries(item.children, level, onRun)}
        </MenuList>
      </Paper>
    </Popper>
  );
}

/**
 * The entries of a menu level, as an array of direct children (MUI's MenuList needs the items as
 * its own children for keyboard navigation and autofocus).
 * @param {Array} items normalized items (utils.normalizeItems)
 * @param {ReturnType<typeof useSubmenuLevel>} level
 * @param {(item: object, event: Event) => void} onRun
 */
export function renderEntries(items, level, onRun) {
  const withIcons = items.some((item) => item.icon || item.checked !== undefined);
  const out = [];
  items.forEach((item, index) => {
    const key = `${item.id}-${index}`;
    if (item.divider) {
      out.push(<Divider key={key} component="li" sx={{ my: "4px !important" }} />);
      return;
    }
    if (item.header) {
      out.push(
        <ListSubheader
          key={key}
          disableSticky
          sx={{
            px: 1.25,
            pt: index === 0 ? 0.5 : 1,
            pb: 0.5,
            lineHeight: "20px",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: colors.gray500,
            bgcolor: "transparent",
          }}
        >
          {item.label}
        </ListSubheader>
      );
      return;
    }

    const hasChildren = Array.isArray(item.children) && item.children.length > 0;
    const isOpen = hasChildren && level.sub?.id === item.id;
    const checkable = item.checked !== undefined;
    const shortcut = shortcutText(item.shortcut);

    const handleClick = (event) => {
      if (item.disabled) return;
      if (hasChildren) {
        level.openSub(item.id, event.currentTarget, event.detail === 0);
        return;
      }
      onRun(item, event);
    };
    const handleKeyDown = (event) => {
      if (!hasChildren || item.disabled) return;
      // Enter and Space reach handleClick through MUI's ButtonBase
      if (event.key === "ArrowRight") {
        event.preventDefault();
        event.stopPropagation();
        level.openSub(item.id, event.currentTarget, true);
      }
    };
    const handleMouseEnter = (event) => {
      if (hasChildren && !item.disabled) level.openSub(item.id, event.currentTarget);
      else if (level.sub) level.scheduleClose();
    };

    out.push(
      <MenuItem
        key={key}
        disabled={item.disabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        role={checkable ? "menuitemcheckbox" : "menuitem"}
        aria-checked={checkable ? !!item.checked : undefined}
        aria-haspopup={hasChildren ? "menu" : undefined}
        aria-expanded={hasChildren ? isOpen : undefined}
        selected={isOpen}
        data-danger={item.danger ? "true" : undefined}
        sx={
          item.danger
            ? {
                color: colors.danger,
                "& .MuiListItemIcon-root": { color: colors.danger },
                "&:hover, &.Mui-focusVisible": { backgroundColor: colors.dangerSoft },
              }
            : undefined
        }
      >
        {withIcons && (
          <ListItemIcon>
            {checkable && item.checked ? <Check fontSize="small" /> : renderIcon(item.icon)}
          </ListItemIcon>
        )}
        <ListItemText
          primary={item.label}
          secondary={item.description}
          slotProps={{
            primary: {
              sx: { fontSize: 14, fontWeight: 400, color: "inherit", lineHeight: "20px" },
            },
            secondary: {
              sx: { fontSize: 12, color: colors.gray500, lineHeight: "16px", whiteSpace: "normal" },
            },
          }}
        />
        {shortcut && !hasChildren && (
          <Box component="kbd" aria-hidden="true" sx={shortcutSx}>
            {shortcut}
          </Box>
        )}
        {hasChildren && (
          <ChevronRight fontSize="small" sx={{ ml: 2, mr: -0.5, color: colors.gray400 }} />
        )}
      </MenuItem>
    );

    if (isOpen) {
      out.push(
        <Submenu
          key={`${key}-submenu`}
          item={item}
          anchorEl={level.sub.anchorEl}
          focusFirst={level.sub.focusFirst}
          onRun={onRun}
          onPointerEnter={level.cancel}
          onRequestClose={(focusParent) => {
            const anchor = level.sub?.anchorEl;
            level.closeSub();
            if (focusParent) anchor?.focus?.();
          }}
        />
      );
    }
  });
  return out;
}
